// Map Marker Components

import React, { useState } from 'react';
import { Marker, Popup } from 'react-leaflet';

import { depotIcon, endDepotIcon, createCustomerIcon, createSimpleCustomerIcon, breakIcon, getVehicleColor } from './map-utils';
import { Customer, Route } from '../../../../api/mobiApi/domain/domain.types';

interface DepotMarkerProps {
  depot: { lat: number; lon: number; name?: string };
}

export const DepotMarker: React.FC<DepotMarkerProps> = ({ depot }) => (
  <Marker position={[depot.lat, depot.lon]} icon={depotIcon}>
    <Popup>
      <div className="p-2">
        <div className="font-bold text-lg">🟢 {depot.name || 'Başlangıç Deposu'}</div>
        <div className="text-sm">Araçların başlangıç noktası</div>
        <div className="text-xs text-gray-500 mt-1">
          📍 {depot.lat.toFixed(6)}, {depot.lon.toFixed(6)}
        </div>
      </div>
    </Popup>
  </Marker>
);

interface EndDepotMarkerProps {
  depot: { lat: number; lon: number; name?: string };
}

export const EndDepotMarker: React.FC<EndDepotMarkerProps> = ({ depot }) => (
  <Marker position={[depot.lat, depot.lon]} icon={endDepotIcon}>
    <Popup>
      <div className="p-2">
        <div className="font-bold text-lg">🔴 {depot.name || 'Bitiş Deposu'}</div>
        <div className="text-sm">Araçların dönüş noktası</div>
        <div className="text-xs text-gray-500 mt-1">
          📍 {depot.lat.toFixed(6)}, {depot.lon.toFixed(6)}
        </div>
      </div>
    </Popup>
  </Marker>
);

interface SimpleCustomerMarkersProps {
  customers: Customer[];
  onCustomerClick?: (customer: Customer) => void;
  onTimeWindowChange?: (customerId: string, timeWindow: { start: string; end: string } | undefined) => void;
  onPriorityChange?: (customerId: string, priority: number | undefined) => void;
}

export const SimpleCustomerMarkers: React.FC<SimpleCustomerMarkersProps> = ({ customers, onCustomerClick, onTimeWindowChange, onPriorityChange }) => {
  // Local state for time inputs (per customer)
  const [timeInputs, setTimeInputs] = useState<Record<string, { start: string; end: string }>>({});
  const [priorityInputs, setPriorityInputs] = useState<Record<string, number>>({});

  const handleTimeInputChange = (customerId: string, field: 'start' | 'end', value: string) => {
    setTimeInputs(prev => ({
      ...prev,
      [customerId]: {
        start: field === 'start' ? value : (prev[customerId]?.start || ''),
        end: field === 'end' ? value : (prev[customerId]?.end || '')
      }
    }));
  };

  const handleTimeBlur = (customerId: string, customer: Customer) => {
    if (!onTimeWindowChange) return;

    const localInput = timeInputs[customerId];
    if (localInput) {
      // Save the time window when user finishes editing
      if (localInput.start || localInput.end) {
        onTimeWindowChange(customerId, localInput as any);
      }
    }
  };

  const handleClearTimeWindow = (customerId: string) => {
    if (onTimeWindowChange) {
      onTimeWindowChange(customerId, undefined);
      setTimeInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[customerId];
        return newInputs;
      });
    }
  };

  const handlePriorityChange = (customerId: string, value: string) => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1 || numValue > 10) return;

    setPriorityInputs(prev => ({
      ...prev,
      [customerId]: numValue
    }));
  };

  const handlePriorityBlur = (customerId: string) => {
    if (!onPriorityChange) return;

    const priority = priorityInputs[customerId];
    if (priority !== undefined) {
      onPriorityChange(customerId, priority);
    }
  };

  const handleClearPriority = (customerId: string) => {
    if (onPriorityChange) {
      onPriorityChange(customerId, undefined);
      setPriorityInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[customerId];
        return newInputs;
      });
    }
  };

  return (
    <>
      {customers.map((customer, index) => {
        if (!Number.isFinite(customer.lat) || !Number.isFinite(customer.lon)) {
          return null;
        }

        return (
          <Marker
            key={`customer-${customer.id}`}
            position={[customer.lat, customer.lon]}
            icon={createSimpleCustomerIcon()}
            eventHandlers={{
              click: () => {
                if (onCustomerClick) {
                  onCustomerClick(customer);
                }
              }
            }}
          >
            <Popup minWidth={180}>
              <div style={{
                padding: '0',
                fontFamily: 'Arial, sans-serif'
              }}>
                {/* Header with gradient */}
                <div style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  padding: '6px 8px',
                  borderRadius: '3px 3px 0 0',
                  marginBottom: '4px'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: 'white',
                    marginBottom: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span style={{ fontSize: '13px' }}>📍</span>
                    {customer.name}
                  </div>
                  {customer.address && (
                    <div style={{
                      fontSize: '9px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      lineHeight: '1.2'
                    }}>
                      {customer.address}
                    </div>
                  )}
                </div>

                {/* Info cards */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  padding: '0 6px 6px 6px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 6px',
                    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                    borderRadius: '3px',
                    border: '1px solid #93c5fd'
                  }}>
                    <span style={{ fontSize: '13px' }}>📦</span>
                    <div>
                      <div style={{
                        fontSize: '7px',
                        fontWeight: '600',
                        color: '#1e40af',
                        marginBottom: '1px'
                      }}>Talep Miktarı</div>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: '800',
                        color: '#1e3a8a'
                      }}>{customer.demand || 200} kg</div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 6px',
                    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                    borderRadius: '3px',
                    border: '1px solid #d1d5db'
                  }}>
                    <span style={{ fontSize: '11px' }}>🗺️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '7px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '1px'
                      }}>Koordinatlar</div>
                      <div style={{
                        fontSize: '8px',
                        fontWeight: '700',
                        color: '#1f2937',
                        fontFamily: 'monospace'
                      }}>{customer.lat.toFixed(5)}, {customer.lon.toFixed(5)}</div>
                    </div>
                  </div>

                  {/* Time Window Section - Modern Design */}
                  <div style={{
                    marginTop: '0px',
                    padding: '6px',
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    borderRadius: '3px',
                    border: '1px solid #fbbf24',
                    boxShadow: '0 1px 2px rgba(251, 191, 36, 0.1)'
                  }}>
                    <div style={{
                      fontSize: '8px',
                      fontWeight: '700',
                      color: '#78350f',
                      marginBottom: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      <span style={{ fontSize: '9px' }}>🕐</span>
                      Teslimat Saati
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto 1fr',
                      gap: '6px',
                      alignItems: 'center',
                      marginBottom: customer.time_window ? '6px' : '0'
                    }}>
                      <div>
                        <label style={{
                          fontSize: '9px',
                          fontWeight: '600',
                          color: '#92400e',
                          display: 'block',
                          marginBottom: '3px'
                        }}>Başlangıç</label>
                        <input
                          type="time"
                          value={timeInputs[customer.id]?.start ?? customer.time_window?.start ?? ''}
                          onChange={(e) => handleTimeInputChange(customer.id, 'start', e.target.value)}
                          onBlur={() => handleTimeBlur(customer.id, customer)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#f59e0b';
                            // Initialize local state with current value when focused
                            if (!timeInputs[customer.id]) {
                              setTimeInputs(prev => ({
                                ...prev,
                                [customer.id]: {
                                  start: customer.time_window?.start || '',
                                  end: customer.time_window?.end || ''
                                }
                              }));
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '5px 4px',
                            border: '1.5px solid #fbbf24',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: 'white',
                            color: '#78350f',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        />
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: '#92400e',
                        marginTop: '12px'
                      }}>→</div>
                      <div>
                        <label style={{
                          fontSize: '9px',
                          fontWeight: '600',
                          color: '#92400e',
                          display: 'block',
                          marginBottom: '3px'
                        }}>Bitiş</label>
                        <input
                          type="time"
                          value={timeInputs[customer.id]?.end ?? customer.time_window?.end ?? ''}
                          onChange={(e) => handleTimeInputChange(customer.id, 'end', e.target.value)}
                          onBlur={() => handleTimeBlur(customer.id, customer)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#f59e0b';
                            // Initialize local state with current value when focused
                            if (!timeInputs[customer.id]) {
                              setTimeInputs(prev => ({
                                ...prev,
                                [customer.id]: {
                                  start: customer.time_window?.start || '',
                                  end: customer.time_window?.end || ''
                                }
                              }));
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '5px 4px',
                            border: '1.5px solid #fbbf24',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: 'white',
                            color: '#78350f',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        />
                      </div>
                    </div>
                    {customer.time_window && customer.time_window.start && customer.time_window.end && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearTimeWindow(customer.id);
                        }}
                        style={{
                          width: '100%',
                          padding: '4px 8px',
                          fontSize: '10px',
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '3px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
                      >
                        <span>✕</span>
                        <span>Temizle</span>
                      </button>
                    )}
                  </div>

                  {/* Priority Section - Modern Design */}
                  <div style={{
                    marginTop: '0px',
                    padding: '6px',
                    background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                    borderRadius: '3px',
                    border: '1px solid #f9a8d4',
                    boxShadow: '0 1px 2px rgba(249, 168, 212, 0.1)'
                  }}>
                    <div style={{
                      fontSize: '8px',
                      fontWeight: '700',
                      color: '#831843',
                      marginBottom: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      <span style={{ fontSize: '9px' }}>⭐</span>
                      Öncelik (1-10)
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      alignItems: 'center'
                    }}>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={priorityInputs[customer.id] ?? customer.priority ?? ''}
                        onChange={(e) => handlePriorityChange(customer.id, e.target.value)}
                        onBlur={() => handlePriorityBlur(customer.id)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#ec4899';
                          // Initialize local state with current value when focused
                          if (priorityInputs[customer.id] === undefined && customer.priority) {
                            setPriorityInputs(prev => ({
                              ...prev,
                              [customer.id]: customer.priority!
                            }));
                          }
                        }}
                        placeholder="Öncelik"
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          border: '1.5px solid #f9a8d4',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontWeight: '600',
                          background: 'white',
                          color: '#831843',
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      />
                      {(customer.priority !== undefined && customer.priority !== null) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearPriority(customer.id);
                          }}
                          style={{
                            padding: '6px 10px',
                            fontSize: '10px',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div style={{
                      fontSize: '9px',
                      color: '#9f1239',
                      marginTop: '4px'
                    }}>
                      10 = En yüksek öncelik
                    </div>
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

interface ExtendedCustomer {
  lat: number;
  lon: number;
  sequence_order: number;
  vehicle_id: string;
  vehicle_type: string;
  route_index: number;
  customer_name: string;
  customer_id?: string;
  name?: string;
  demand: number;
  location_type: string;
  address?: string;
  time_window?: {
    start: string;
    end: string;
  };
  [key: string]: any;
}

interface RouteCustomerMarkersProps {
  routes: Route[];
  selectedRoute: string | null;
  onTimeWindowChange?: (customerId: string, timeWindow: { start: string; end: string } | undefined) => void;
  onPriorityChange?: (customerId: string, priority: number | undefined) => void;
}

export const RouteCustomerMarkers: React.FC<RouteCustomerMarkersProps> = ({ routes, selectedRoute, onTimeWindowChange, onPriorityChange }) => {
  // Local state for time inputs (per customer)
  const [timeInputs, setTimeInputs] = useState<Record<string, { start: string; end: string }>>({});
  const [priorityInputs, setPriorityInputs] = useState<Record<string, number>>({});

  const handleTimeInputChange = (customerId: string, field: 'start' | 'end', value: string) => {
    setTimeInputs(prev => ({
      ...prev,
      [customerId]: {
        start: field === 'start' ? value : (prev[customerId]?.start || ''),
        end: field === 'end' ? value : (prev[customerId]?.end || '')
      }
    }));
  };

  const handleTimeBlur = (customerId: string, currentTimeWindow?: { start: string; end: string }) => {
    if (!onTimeWindowChange) return;

    const localInput = timeInputs[customerId];
    if (localInput) {
      // Save the time window when user finishes editing
      if (localInput.start || localInput.end) {
        onTimeWindowChange(customerId, localInput as any);
      }
    }
  };

  const handleClearTimeWindow = (customerId: string) => {
    if (onTimeWindowChange) {
      onTimeWindowChange(customerId, undefined);
      setTimeInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[customerId];
        return newInputs;
      });
    }
  };

  const handlePriorityChange = (customerId: string, value: string) => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1 || numValue > 10) return;

    setPriorityInputs(prev => ({
      ...prev,
      [customerId]: numValue
    }));
  };

  const handlePriorityBlur = (customerId: string) => {
    if (!onPriorityChange) return;

    const priority = priorityInputs[customerId];
    if (priority !== undefined) {
      onPriorityChange(customerId, priority);
    }
  };

  const handleClearPriority = (customerId: string) => {
    if (onPriorityChange) {
      onPriorityChange(customerId, undefined);
      setPriorityInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[customerId];
        return newInputs;
      });
    }
  };

  const allMarkers: ExtendedCustomer[] = routes.flatMap((route, routeIndex) =>
    (route.route_sequence || [])
      .filter(customer => customer && (
        customer.location_type === 'CUSTOMER' ||
        customer.location_type === 'customer' ||
        customer.location_type === 'BREAK' ||
        customer.location_type === 'break'
      ))
      .map(customer => {
        // Ensure sequence_order is a valid positive number
        const sequenceOrder = typeof customer.sequence_order === 'number' &&
                              !isNaN(customer.sequence_order) &&
                              customer.sequence_order >= 0
          ? customer.sequence_order
          : 0;

        const marker = {
          ...customer,
          sequence_order: sequenceOrder,
          vehicle_id: route.vehicle_id,
          vehicle_type: route.vehicle_type,
          route_index: routeIndex
        };
        // Debug: log time_window
        if (marker.time_window) {
          console.log(`🕐 Marker with time_window: ${marker.customer_name || marker.name}`, marker.time_window);
        }
        return marker;
      })
  ).sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));

  const coordCounts: Record<string, number> = {};

  return (
    <>
      {allMarkers.map((customer) => {
        const lat = customer.lat;
        const lon = customer.lon;
        if (!lat || !lon || !Number.isFinite(lat) || !Number.isFinite(lon)) {
          return null;
        }

        const key = `${lat.toFixed(6)},${lon.toFixed(6)}`;
        const count = coordCounts[key] || 0;
        coordCounts[key] = count + 1;

        const jitterDelta = 0.00008 * count;
        const position: [number, number] = [
          lat + jitterDelta,
          lon + jitterDelta
        ];

        const vehicleId = parseInt(customer.vehicle_id.replace('vehicle_', ''));
        const routeColor = getVehicleColor(vehicleId);
        const isSelected = selectedRoute === customer.vehicle_id;
        const isAnyRouteSelected = selectedRoute !== null;
        const isBreak = customer.location_type === 'BREAK' || customer.location_type === 'break';

        // If a route is selected, make non-selected markers very faint
        const markerOpacity = isAnyRouteSelected
          ? (isSelected ? 1.0 : 0.25)
          : 1.0;

        // Use break icon for break locations, customer icon for customers
        const markerIcon = isBreak
          ? breakIcon
          : createCustomerIcon(customer.sequence_order, routeColor);

        return (
          <Marker
            key={`route-${isBreak ? 'break' : 'customer'}-${customer.vehicle_id}-${customer.sequence_order}`}
            position={position}
            icon={markerIcon}
            opacity={markerOpacity}
            zIndexOffset={isSelected ? 1000 : 100}
          >
            <Popup minWidth={190}>
              <div style={{
                padding: '0',
                fontFamily: 'Arial, sans-serif'
              }}>
                {/* Header with gradient and route color */}
                <div style={{
                  background: isBreak
                    ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                    : `linear-gradient(135deg, ${routeColor} 0%, ${routeColor}dd 100%)`,
                  padding: '6px 8px',
                  borderRadius: '3px 3px 0 0',
                  marginBottom: '4px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Sequence badge */}
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    right: '6px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    color: isBreak ? '#16a34a' : routeColor,
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: '800',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
                  }}>
                    {customer.sequence_order}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: 'white',
                    marginBottom: '2px',
                    paddingRight: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span style={{ fontSize: '13px' }}>{isBreak ? '☕' : '📍'}</span>
                    {customer.customer_name || customer.name || 'Mola Noktası'}
                  </div>
                  {customer.address && (
                    <div style={{
                      fontSize: '9px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      lineHeight: '1.2'
                    }}>
                      {customer.address}
                    </div>
                  )}
                </div>

                {/* Info cards */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  padding: '0 6px 6px 6px'
                }}>
                  {isBreak ? (
                    <>
                      {/* Break duration */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '5px 6px',
                        background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                        borderRadius: '3px',
                        border: '1px solid #6ee7b7'
                      }}>
                        <span style={{ fontSize: '13px' }}>⏱️</span>
                        <div>
                          <div style={{
                            fontSize: '7px',
                            fontWeight: '600',
                            color: '#065f46',
                            marginBottom: '1px'
                          }}>Mola Süresi</div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: '800',
                            color: '#047857'
                          }}>{customer.service_duration_formatted || `${Math.floor((customer.service_duration || 0) / 60)} dk`}</div>
                        </div>
                      </div>

                      {/* Arrival time */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '5px 6px',
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderRadius: '3px',
                        border: '1px solid #fbbf24'
                      }}>
                        <span style={{ fontSize: '13px' }}>🕐</span>
                        <div>
                          <div style={{
                            fontSize: '7px',
                            fontWeight: '600',
                            color: '#92400e',
                            marginBottom: '1px'
                          }}>Varış Saati</div>
                          <div style={{
                            fontSize: '10px',
                            fontWeight: '700',
                            color: '#78350f'
                          }}>{customer.arrival_time_formatted || 'N/A'}</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Customer demand */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '5px 6px',
                        background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                        borderRadius: '3px',
                        border: '1px solid #93c5fd'
                      }}>
                        <span style={{ fontSize: '13px' }}>📦</span>
                        <div>
                          <div style={{
                            fontSize: '7px',
                            fontWeight: '600',
                            color: '#1e40af',
                            marginBottom: '1px'
                          }}>Talep Miktarı</div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: '800',
                            color: '#1e3a8a'
                          }}>{customer.demand || 0} kg</div>
                        </div>
                      </div>

                      {/* Vehicle type */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '5px 6px',
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderRadius: '3px',
                        border: '1px solid #fbbf24'
                      }}>
                        <span style={{ fontSize: '13px' }}>🚚</span>
                        <div>
                          <div style={{
                            fontSize: '7px',
                            fontWeight: '600',
                            color: '#92400e',
                            marginBottom: '1px'
                          }}>Araç Tipi</div>
                          <div style={{
                            fontSize: '10px',
                            fontWeight: '700',
                            color: '#78350f'
                          }}>{customer.vehicle_type}</div>
                        </div>
                      </div>
                    </>
                  )}

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 6px',
                    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                    borderRadius: '3px',
                    border: '1px solid #d1d5db'
                  }}>
                    <span style={{ fontSize: '11px' }}>🗺️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '7px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '1px'
                      }}>Koordinatlar</div>
                      <div style={{
                        fontSize: '8px',
                        fontWeight: '700',
                        color: '#1f2937',
                        fontFamily: 'monospace'
                      }}>{lat.toFixed(5)}, {lon.toFixed(5)}</div>
                    </div>
                  </div>

                  {/* Time Window Section - Modern Design */}
                  {customer.customer_id && (
                    <div style={{
                      marginTop: '0px',
                      padding: '6px',
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      borderRadius: '3px',
                      border: '1px solid #fbbf24',
                      boxShadow: '0 1px 2px rgba(251, 191, 36, 0.1)'
                    }}>
                      <div style={{
                        fontSize: '8px',
                        fontWeight: '700',
                        color: '#78350f',
                        marginBottom: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        <span style={{ fontSize: '9px' }}>🕐</span>
                        Teslimat Saati
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto 1fr',
                        gap: '6px',
                        alignItems: 'center',
                        marginBottom: customer.time_window && customer.time_window.start && customer.time_window.end ? '6px' : '0'
                      }}>
                        <div>
                          <label style={{
                            fontSize: '9px',
                            fontWeight: '600',
                            color: '#92400e',
                            display: 'block',
                            marginBottom: '3px'
                          }}>Başlangıç</label>
                          <input
                            type="time"
                            value={timeInputs[customer.customer_id!]?.start ?? customer.time_window?.start ?? ''}
                            onChange={(e) => handleTimeInputChange(customer.customer_id!, 'start', e.target.value)}
                            onBlur={() => handleTimeBlur(customer.customer_id!, customer.time_window)}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#f59e0b';
                              // Initialize local state with current value when focused
                              if (!timeInputs[customer.customer_id!]) {
                                setTimeInputs(prev => ({
                                  ...prev,
                                  [customer.customer_id!]: {
                                    start: customer.time_window?.start || '',
                                    end: customer.time_window?.end || ''
                                  }
                                }));
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '5px 4px',
                              border: '1.5px solid #fbbf24',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: 'white',
                              color: '#78350f',
                              textAlign: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          />
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#92400e',
                          marginTop: '12px'
                        }}>→</div>
                        <div>
                          <label style={{
                            fontSize: '9px',
                            fontWeight: '600',
                            color: '#92400e',
                            display: 'block',
                            marginBottom: '3px'
                          }}>Bitiş</label>
                          <input
                            type="time"
                            value={timeInputs[customer.customer_id!]?.end ?? customer.time_window?.end ?? ''}
                            onChange={(e) => handleTimeInputChange(customer.customer_id!, 'end', e.target.value)}
                            onBlur={() => handleTimeBlur(customer.customer_id!, customer.time_window)}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#f59e0b';
                              // Initialize local state with current value when focused
                              if (!timeInputs[customer.customer_id!]) {
                                setTimeInputs(prev => ({
                                  ...prev,
                                  [customer.customer_id!]: {
                                    start: customer.time_window?.start || '',
                                    end: customer.time_window?.end || ''
                                  }
                                }));
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '5px 4px',
                              border: '1.5px solid #fbbf24',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: 'white',
                              color: '#78350f',
                              textAlign: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          />
                        </div>
                      </div>
                      {customer.time_window && customer.time_window.start && customer.time_window.end && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearTimeWindow(customer.customer_id!);
                          }}
                          style={{
                            width: '100%',
                            padding: '4px 8px',
                            fontSize: '10px',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '3px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
                        >
                          <span>✕</span>
                          <span>Temizle</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Priority Section - Modern Design */}
                  {customer.customer_id && !isBreak && (
                    <div style={{
                      marginTop: '0px',
                      padding: '6px',
                      background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                      borderRadius: '3px',
                      border: '1px solid #f9a8d4',
                      boxShadow: '0 1px 2px rgba(249, 168, 212, 0.1)'
                    }}>
                      <div style={{
                        fontSize: '8px',
                        fontWeight: '700',
                        color: '#831843',
                        marginBottom: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        <span style={{ fontSize: '9px' }}>⭐</span>
                        Öncelik (1-10)
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '6px',
                        alignItems: 'center'
                      }}>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={priorityInputs[customer.customer_id!] ?? customer.priority ?? ''}
                          onChange={(e) => handlePriorityChange(customer.customer_id!, e.target.value)}
                          onBlur={() => handlePriorityBlur(customer.customer_id!)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#ec4899';
                            // Initialize local state with current value when focused
                            if (priorityInputs[customer.customer_id!] === undefined && customer.priority) {
                              setPriorityInputs(prev => ({
                                ...prev,
                                [customer.customer_id!]: customer.priority!
                              }));
                            }
                          }}
                          placeholder="Öncelik"
                          style={{
                            flex: 1,
                            padding: '6px 8px',
                            border: '1.5px solid #f9a8d4',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: '600',
                            background: 'white',
                            color: '#831843',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        />
                        {(customer.priority !== undefined && customer.priority !== null) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearPriority(customer.customer_id!);
                            }}
                            style={{
                              padding: '6px 10px',
                              fontSize: '10px',
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div style={{
                        fontSize: '9px',
                        color: '#9f1239',
                        marginTop: '4px'
                      }}>
                        10 = En yüksek öncelik
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};
